import { ChangeEvent, FC } from "react";
import styles from "./ReportHeader.module.scss";
import {
  DealerKeysEnum,
  IReportProduct,
  ReportDealerInfoType,
  ReportType,
} from "@/entities/report/types";
import {
  TableInfoType,
  FilialsTableInfoType,
  ProductTotalsType,
  FilialInfoType,
} from "@/widgets/tables/reportTable/types/types";
import clsx from "clsx";

interface ReportHeaderProps {
  el:
    | keyof typeof DealerKeysEnum
    | "productionPlanQuantity"
    | "remainsQuantity";
  product: IReportProduct;
  dealer: ReportDealerInfoType;
  reportStatus: ReportType;
  isDraft: boolean;
  totalView?: boolean;
  isFilialView?: boolean;
  productsDealerData: TableInfoType | null;
  reservedData: FilialsTableInfoType | null;
  productTotals?: ProductTotalsType;
  productDealerFilialTotals: FilialInfoType;
  dealerMainId: string;
  hasBranch: boolean;
  selectedProductId: string;
  dealerOrderItem?: ReportDealerInfoType;
  getMainDealerTotalValue: (
    productId: string,
    dealer: ReportDealerInfoType,
    productData: TableInfoType | null,
    keyName: keyof ReportDealerInfoType,
  ) => number;
  handleBlur: (
    e: ChangeEvent<HTMLInputElement>,
    tempValue: number,
    dealerOrderItem: ReportDealerInfoType,
    hasBranch: boolean,
  ) => void;
}

export const ReportHeader: FC<ReportHeaderProps> = ({
  product,
  dealer,
  el,
  totalView = false,
  isFilialView = false,
  reportStatus,
  isDraft,
  productsDealerData,
  productTotals,
  reservedData,
  productDealerFilialTotals,
  dealerMainId,
  hasBranch,
  selectedProductId,
  dealerOrderItem,
  getMainDealerTotalValue,
  handleBlur,
}) => {
  const itemInfoObj =
    productsDealerData && productsDealerData[product.vendorId];
  const distributionValue = isFilialView
    ? reservedData?.[product.vendorId]?.[dealer.dealerId] ||
      reservedData?.[product.vendorId]?.[dealerMainId]
    : (product.productionPlanQuantity ?? 0);

  const dealerInfoValue = (itemInfoObj && itemInfoObj[dealer.dealerId]) || {
    quantity: 0,
    preQuantityByDealerVendor: 0,
  };

  const itemTotal = (productTotals && productTotals[product.vendorId]) || {
    quantity: 0,
    requestedQuantity: 0,
    shippedQuantity: 0,
    reservedQuantity: 0,
  };
  const defaultValue =
    isFilialView || (!isFilialView && !hasBranch)
      ? parseFloat(dealerInfoValue.quantity.toString())
      : parseFloat((dealerInfoValue.preQuantityByDealerVendor ?? 0).toString());
  const productTotal = isFilialView
    ? (productDealerFilialTotals?.[product.vendorId] ?? 0)
    : (itemTotal?.quantity ?? 0);

  const canDistribute =
    (dealerOrderItem?.canDistribute && !dealerOrderItem.disabled) || false;
  const onWheelHandler = () => (document.activeElement as HTMLElement).blur();
  const inputMaxValue = product.productionPlanQuantity ?? 0;

  if (totalView && productTotals && itemTotal) {
    return (
      <td key={product.vendorId + el}>
        {el === "requestedQuantity" && itemTotal.requestedQuantity}
        {el === "shippedQuantity" && itemTotal.shippedQuantity}
        {el === "reservedQuantity" && itemTotal.reservedQuantity}
        {el === "productionPlanQuantity" && distributionValue}
        {el === "ensuredQuantity" && (
          <>
            {distributionValue}
            {isDraft && (
              <>
                /
                <span
                  className={
                    productTotal !== distributionValue
                      ? styles.redValue
                      : styles.greenValue
                  }
                >
                  {productTotal}
                </span>
              </>
            )}
          </>
        )}
      </td>
    );
  }
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (+e.target.value > +e.target.max) {
      e.target.value = e.target.max;
    }
    if (+e.target.value < 0) {
      e.target.value = "0";
    }
    if (
      +e.target.value >= +e.target.max &&
      (!hasBranch || (hasBranch && isFilialView))
    ) {
      e.target.value = e.target.max;
    }
    const value = parseFloat(e.target.value) ? parseFloat(e.target.value) : 0;

    handleBlur(e, value, dealer as ReportDealerInfoType, hasBranch);
  };
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const element = e.target as HTMLInputElement;

    if (element.value === "0") {
      element.value = "";
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

  const showEnsuredQuantityInput =
    el === "ensuredQuantity" && isDraft && productTotals && dealerInfoValue;

  return (
    <td
      key={el + product.vendorId + el}
      className={clsx({
        [styles.selectedRow]: selectedProductId === product.vendorId,
      })}
    >
      {!showEnsuredQuantityInput &&
        (((el !== "ensuredQuantity" &&
          el !== "quantity" &&
          isDraft &&
          reportStatus !== "plan") ||
          !isDraft) &&
        dealerInfoValue &&
        dealer &&
        (!dealer.branches?.length || isFilialView)
          ? dealerInfoValue[el as keyof ReportDealerInfoType]
          : getMainDealerTotalValue(
              product.vendorId,
              dealer,
              productsDealerData,
              el as keyof ReportDealerInfoType,
            ))}
      {showEnsuredQuantityInput && (
        <div
          className={clsx(styles.inputRow, {
            [styles.disabled]:
              (!distributionValue && defaultValue === 0) || !canDistribute,
          })}
        >
          <input type="text" value={inputMaxValue + "/"} readOnly />
          <input
            key={product.vendorId + dealer.dealerId}
            id={product.vendorId}
            data-orderitemdraftid={dealerInfoValue.orderItemDraftId}
            max={inputMaxValue}
            type="number"
            min={0}
            defaultValue={defaultValue.toString()}
            disabled={
              (!distributionValue && defaultValue === 0) || !canDistribute
            }
            onWheel={onWheelHandler}
            onClick={handleClick}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}
    </td>
  );
};
